import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, api } from '../api';
import type { Superuser } from '../api';
import { useSuperuserSession } from '../auth';
import { Banner, Field, Modal, Spinner } from '../components/ui';

/** Note 2: only a superuser can create another superuser. */
export function Superusers() {
  const { token, superuser } = useSuperuserSession();
  const [rows, setRows] = useState<Superuser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<Superuser | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listSuperusers(token);
      setRows(res.superusers);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load superadmins');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onDelete() {
    if (!removing) return;
    try {
      await api.deleteSuperuser(token, removing.id);
      setRemoving(null);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the superadmin');
      setRemoving(null);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Superadmins</h1>
          <p className="muted">Superadmins manage applications and can create other superadmins.</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
          New superadmin
        </button>
      </div>

      {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}

      {loading ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.email}
                    {row.id === superuser.id && <span className="pill pill--muted">You</span>}
                  </td>
                  <td className="muted small">{new Date(row.createdAt).toLocaleString()}</td>
                  <td className="right">
                    {row.id !== superuser.id && rows.length > 1 && (
                      <button type="button" className="btn btn--small btn--danger" onClick={() => setRemoving(row)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateSuperuserModal
          token={token}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}

      {removing && (
        <Modal
          title="Delete superadmin"
          onClose={() => setRemoving(null)}
          onSubmit={(e) => {
            e.preventDefault();
            void onDelete();
          }}
          submitLabel="Delete"
          destructive
        >
          <Banner kind="warn">
            Remove superadmin access for <strong>{removing.email}</strong>?
          </Banner>
        </Modal>
      )}
    </div>
  );
}

function CreateSuperuserModal({
  token,
  onClose,
  onCreated,
}: {
  token: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.createSuperuser(token, email.trim(), password);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the superadmin');
      setSubmitting(false);
    }
  }

  return (
    <Modal title="New superadmin" onClose={onClose} onSubmit={onSubmit} submitLabel="Create" submitting={submitting}>
      {error && <Banner kind="error">{error}</Banner>}
      <Field label="Email">
        <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field label="Password" hint="At least 8 characters with an uppercase letter, a lowercase letter and a digit.">
        <input
          className="input"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
      </Field>
    </Modal>
  );
}
